import React, { useState, useEffect } from "react";
import {
  Box,
  Grid,
  TextField,
  Select,
  MenuItem,
  Button,
  FormControl,
  InputLabel,
  Stack,
  Checkbox,
  ListItemText,
} from "@mui/material";
import { DatePicker } from "@mui/x-date-pickers";
import { DataGrid, GridToolbar } from "@mui/x-data-grid";
import moment from "moment";
import { useSelector } from "react-redux";

import {
  fAlertToast,
  fcheck_null_undefine_replace_empty,
} from "../../Utility/Utilitys";
import CouponDetail from "./CouponDetail";
import { SPL_PromoCouponHead_Summary } from "../../Server/ApiV2";

// DataGrid Columns
const columns = [
  { field: "A_PromotionID", headerName: "Promo ID", width: 80 },
  {
    field: "A_FromDate",
    headerName: "Start Date",
    width: 90,
    valueFormatter: ({ value }) => moment(value).format("DD/MM/YYYY"),
  },
  {
    field: "A_ToDate",
    headerName: "End Date",
    width: 90,
    valueFormatter: ({ value }) => moment(value).format("DD/MM/YYYY"),
  },
  { field: "Loc_Name", headerName: "Loc", width: 150 },
  { field: "A_PurchaseAmount", headerName: "Purchase Amt", width: 75 },
  { field: "A_RedemptionLimit", headerName: "Redemption Limit", width: 75 },
  { field: "A_DiscountPercent", headerName: "Disc %", width: 75 },
  { field: "A_IsActive", headerName: "Active", width: 75 },
  { field: "A_MultipleCoupon", headerName: "Multiple Coupon", width: 75 },
  {
    field: "A_CouponValidFrom",
    headerName: "Coupon Valid From",
    width: 90,
    valueFormatter: ({ value }) => moment(value).format("DD/MM/YYYY"),
  },
  {
    field: "A_CouponValideTo",
    headerName: "Coupon Valide To",
    width: 90,
    valueFormatter: ({ value }) => moment(value).format("DD/MM/YYYY"),
  },
  {
    field: "CreatedBy",
    headerName: "Created By",
    width: 150,
  },
  {
    field: "CreatedDate",
    headerName: "Created Date",
    width: 95,
    valueFormatter: ({ value }) => moment(value).format("DD/MM/YYYY"),
  },
];

const CouponSummary = () => {
  // Dropdown locations
  const LocWithRights = useSelector(
    (state) => state.SliceDB.value.Location.LocWithRights
  );
  const [selectedRow, setSelectedRow] = useState(null);
  // State for filters (single object)
  const [filters, setFilters] = useState({
    startDate: moment().format("YYYY-MM-DD"),
    endDate: moment().format("YYYY-MM-DD"),
    location: LocWithRights[0] ? [LocWithRights[0].Uid] : [],
  });

  // Validation state
  const [errors, setErrors] = useState({
    startDate: false,
    endDate: false,
    location: false,
  });

  const [promoData, setPromoData] = useState([]);
  // State for modal
  const [open, setOpen] = useState(false);
  // Handle filter change
  const handleFilterChange = (key, value) => {
    setFilters((prev) => ({ ...prev, [key]: value }));
    setErrors((prev) => ({ ...prev, [key]: false })); // Clear errors on input change
  };

  // Validate Filters
  const validateFilters = () => {
    const newErrors = {
      startDate: !filters.startDate,
      endDate: !filters.endDate,
      location: !filters.location,
    };
    setErrors(newErrors);
    return !Object.values(newErrors).includes(true);
  };

  // Handle API Call
  const handleLoadData = async () => {
    if (!validateFilters()) return;

    const filterData = {
      P_FromDate: moment(filters.startDate).format("MM-DD-YYYY"),
      P_ToDate: moment(filters.endDate).format("MM-DD-YYYY"),
      Location: filters.location.toString(),
    };
    const response = await SPL_PromoCouponHead_Summary(
      fcheck_null_undefine_replace_empty(filterData)
    );
    if (response?.status === 200 && response?.data?.recordset) {
      setPromoData(response?.data?.recordset); // Fallback to dummy data
    } else {
      setPromoData([]); // Use dummy data on API error
    }
  };

  // Open modal on row click
  const handleRowClick = async (params) => {
    setOpen(true);
    setSelectedRow(params.row);
  };

  const handleModalClose = () => {
    setOpen(false);
  };
  // Fetch locations on mount
  useEffect(() => {
    handleLoadData();
    document.getElementById("startDate")?.focus();
  }, []);

  return (
    <Box
      sx={{
        p: 1,
        width: "100%",
      }}
    >
      <Grid container spacing={1} alignItems="center">
        {/* Start Date */}
        <Grid item xs={6} sm={2.5} md={2}>
          <DatePicker
            label="Start Date"
            value={filters.startDate}
            onChange={(newValue) => handleFilterChange("startDate", newValue)}
            renderInput={(params) => (
              <TextField
                {...params}
                fullWidth
                size="small"
                id="startDate"
                error={errors.startDate}
                helperText={errors.startDate ? "StartDate is required" : ""}
              />
            )}
          />
        </Grid>
        {/* End Date */}
        <Grid item xs={6} sm={2.5} md={2}>
          <DatePicker
            label="End Date"
            value={filters.endDate}
            onChange={(newValue) => handleFilterChange("endDate", newValue)}
            renderInput={(params) => (
              <TextField
                {...params}
                fullWidth
                size="small"
                error={errors.endDate}
                helperText={errors.endDate ? "EndDate is required" : ""}
              />
            )}
          />
        </Grid>
        {/* Location Dropdown */}
        <Grid item xs={6} sm={4} md={3.5}>
          <FormControl size="small" fullWidth>
            <InputLabel id="location-select-label">Location</InputLabel>
            <Select
              labelId="location-select-label"
              id="location-select"
              multiple
              value={filters.location}
              onChange={(event) => {
                const { value } = event.target;
                if (value.includes("all")) {
                  setFilters((prev) => ({
                    ...prev,
                    location:
                      filters.location.length === LocWithRights.length
                        ? []
                        : LocWithRights.map((loc) => loc.Uid),
                  }));
                  return;
                }
                setFilters((prev) => ({
                  ...prev,
                  location: value,
                }));
              }}
              label="Location"
              displayEmpty
              // inputRef={locationSelectRef}
              renderValue={(selected) => (
                <Box
                  sx={{
                    display: "flex",
                    flexDirection: "column",
                    maxHeight: "20px",
                    overflowY: "auto",
                  }}
                >
                  {selected.map((value) => (
                    <Box
                      key={value}
                      sx={{
                        whiteSpace: "nowrap",
                        padding: "2px 4px",
                        backgroundColor: "#f0f0f0",
                        borderRadius: "4px",
                        margin: "2px 0",
                      }}
                    >
                      {LocWithRights.find((loc) => loc.Uid === value)?.Name1 ||
                        value}
                    </Box>
                  ))}
                </Box>
              )}
              MenuProps={{
                PaperProps: {
                  style: {
                    maxHeight: 200,
                    width: 250,
                  },
                },
              }}
            >
              <MenuItem value="all">
                <Checkbox
                  checked={filters.location.length === LocWithRights.length}
                  indeterminate={
                    filters.location.length > 0 &&
                    filters.location.length < LocWithRights.length
                  }
                />
                <ListItemText primary="Select All" />
              </MenuItem>
              {LocWithRights.map((locationItem) => (
                <MenuItem key={locationItem.Uid} value={locationItem.Uid}>
                  <Checkbox
                    checked={filters.location.includes(locationItem.Uid)}
                  />
                  <ListItemText primary={locationItem.Name1} />
                </MenuItem>
              ))}
            </Select>
          </FormControl>
        </Grid>
        {/* Buttons */}
        <Grid item xs={6} md={2}>
          <Stack direction="row" spacing={2}>
            <Button variant="contained" size="small" onClick={handleLoadData}>
              Load
            </Button>
            <Button
              size="small"
              variant="contained"
              color="primary"
              onClick={() => {
                setSelectedRow(null);
                setOpen(true);
              }}
            >
              New
            </Button>
          </Stack>
        </Grid>
      </Grid>
      <Box
        sx={{
          height: {
            xs: "54vh",
            sm: "62.5vh",
            md: "70.5vh",
            lg: "76.5vh",
            xl: "79vh",
          },
          width: "100%",
          mt: 2,
        }}
      >
        <DataGrid
          rows={promoData ?? []}
          columns={columns}
          components={{
            Toolbar: GridToolbar,
          }}
          headerHeight={40}
          rowHeight={30}
          getRowId={(row) => row.A_PromotionID}
          onRowClick={handleRowClick}
          hideFooter={promoData?.length < 100}
          getRowClassName={(params) =>
            params.indexRelativeToCurrentPage % 2 === 0
              ? "mui-even-row"
              : "mui-odd-row"
          }
          sx={{
            border: 1,
            borderColor: "primary.light",
          }}
        />
      </Box>
      <CouponDetail
        open={open}
        onClose={handleModalClose}
        selectedRow={selectedRow}
        onLoad={handleLoadData}
      />
    </Box>
  );
};

export default CouponSummary;
